import { Button, Group, Paper, Stack, Text } from "@mantine/core";
import { IconFolder } from "@tabler/icons-react";
import { client } from "@/api-client";
import type { FileOutput, FolderOutput } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { FileUploadModal } from "@/components/FileUploadModal";
import { FolderNavigator } from "@/components/FolderNavigator";
import { LibraryItem } from "@/components/LibraryItem";

interface GroupFilesProps {
  groupId: string;
}

export const GroupFiles = ({ groupId }: GroupFilesProps) => {
  const {
    data: group,
    isLoading,
    refetch: refetchGroup,
  } = client.useQuery("get", "/trainer/groups/{groupId}", {
    params: { path: { groupId: groupId } },
  });

  // Fetch files for the group
  const fileIds = group?.files ?? [];
  const { data: fileList, refetch: refetchFiles } = client.useQuery(
    "get",
    "/universal/files-batch/batch-get",
    {
      params: {
        query: {
          fileIds: fileIds,
        },
      },
    },
    {
      enabled: fileIds.length > 0,
    },
  );

  const addFileToGroupMutation = client.useMutation(
    "post",
    "/trainer/groups/{groupId}/add-file",
  );

  const addFolderToGroupMutation = client.useMutation(
    "post",
    "/trainer/groups/{groupId}/add-file",
  );

  const handleAddFileToGroup = async (file: FileOutput) => {
    await addFileToGroupMutation.mutateAsync({
      params: {
        path: {
          groupId: groupId,
        },
      },
      body: {
        fileId: file.id,
      },
    });

    await Promise.all([refetchFiles(), refetchGroup()]);
  };

  const handleAddFolderToGroup = async (folder: FolderOutput | null) => {
    if (!folder) return;
    await addFolderToGroupMutation.mutateAsync({
      params: {
        path: {
          groupId: groupId,
        },
      },
      body: {
        fileId: folder.id,
      },
    });

    await Promise.all([refetchFiles(), refetchGroup()]);
  };

  const removeFileFromGroupMutation = client.useMutation(
    "post",
    "/trainer/groups/{groupId}/remove-file",
  );

  const handleRemoveFileFromGroup = async (fileId: string) => {
    await removeFileFromGroupMutation.mutateAsync({
      params: {
        path: {
          groupId: groupId,
        },
      },
      body: {
        fileId: fileId,
      },
    });

    await Promise.all([refetchFiles(), refetchGroup()]);
  };

  if (isLoading || !group) {
    return <FancyLoader />;
  }

  if (!fileList && fileIds.length > 0) {
    return <FancyLoader />;
  }

  return (
    <Stack>
      <Group>
        <FolderNavigator
          onFileSelect={handleAddFileToGroup}
          onFolderSelect={handleAddFolderToGroup}
        >
          <Button
            variant="outline"
            leftSection={<IconFolder size="1.2em" />}
            style={{ flex: 1 }}
          >
            Pick from Library
          </Button>
        </FolderNavigator>
        <FileUploadModal onSuccess={handleAddFileToGroup} />
      </Group>

      {fileIds.length === 0 ? (
        <Paper p="xl" withBorder>
          <Stack gap="md">
            <Text c="dimmed">
              No files have been shared with this group yet.
            </Text>
          </Stack>
        </Paper>
      ) : (
        <>
          {fileList?.files?.map((file: FileOutput) => (
            <LibraryItem
              key={file.id}
              kind="file"
              item={file}
              readonly={true}
              onDelete={handleRemoveFileFromGroup}
            />
          ))}
          {fileList?.folders?.map((folder: FolderOutput) => (
            <LibraryItem
              key={folder.id}
              kind="folder"
              item={folder}
              readonly={true}
              onDelete={handleRemoveFileFromGroup}
              onFolderClick={() => {}}
            />
          ))}
        </>
      )}
    </Stack>
  );
};
